/**
 * Case Archive Data Models
 * 
 * Type definitions and data structures for Case Archive feature.
 * Used for frontend rendering and API communication.
 */

/**
 * Case Status enum
 */
export const CaseStatus = {
  APPROVED: 'approved',
  PENDING: 'pending',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
};

/**
 * Case Category enum
 */
export const CaseCategory = {
  RECORD_SUSPENSION: 'record_suspension',
  US_ENTRY_WAIVER: 'us_entry_waiver',
  INTERNATIONAL_FILING: 'international_filing',
};

/**
 * Case category display names
 */
export const CaseCategoryLabels = {
  [CaseCategory.RECORD_SUSPENSION]: 'Record Suspension',
  [CaseCategory.US_ENTRY_WAIVER]: 'U.S. Entry Waiver',
  [CaseCategory.INTERNATIONAL_FILING]: 'International Filing',
};

/**
 * @typedef {Object} CaseModel
 * @property {string} caseId - Unique case identifier
 * @property {string} category - Case category (CaseCategory enum)
 * @property {string} summary - Brief anonymized summary
 * @property {string} timeline - Processing timeline description
 * @property {string} outcome - Outcome description
 * @property {string} status - Case status (CaseStatus enum)
 * @property {string} lastUpdated - ISO date string
 * @property {string} yearFiled - Year the case was filed
 * @property {string[]} tags - Searchable tags
 */

/**
 * Sample case data (for development/preview)
 */
export const SAMPLE_CASES = [
  {
    caseId: 'RS-2025-001',
    category: CaseCategory.RECORD_SUSPENSION,
    summary: 'Record Suspension granted for individual with DUI and common assault convictions from 2015. Waiting period completed, all fines paid.',
    timeline: 'Application submitted January 2025, approved June 2025 (6 months)',
    outcome: 'Record Suspension Granted',
    status: CaseStatus.APPROVED,
    lastUpdated: '2025-06-15T00:00:00Z',
    yearFiled: '2025',
    tags: ['dui', 'assault', 'approved', 'record-suspension'],
  },
  {
    caseId: 'EW-2025-002',
    category: CaseCategory.US_ENTRY_WAIVER,
    summary: 'U.S. Entry Waiver approved for individual with lifetime inadmissibility due to 1998 drug possession conviction.',
    timeline: 'Application submitted March 2024, approved March 2025 (12 months)',
    outcome: 'Waiver Approved (5 years)',
    status: CaseStatus.APPROVED,
    lastUpdated: '2025-03-20T00:00:00Z',
    yearFiled: '2024',
    tags: ['drug-possession', 'lifetime-inadmissibility', 'approved', 'entry-waiver'],
  },
  {
    caseId: 'IF-2025-003',
    category: CaseCategory.INTERNATIONAL_FILING,
    summary: 'UAE Police Clearance Certificate obtained for professional relocating to Dubai for employment.',
    timeline: 'Documents submitted February 2025, certificate received March 2025 (4 weeks)',
    outcome: 'Certificate Issued',
    status: CaseStatus.COMPLETED,
    lastUpdated: '2025-03-10T00:00:00Z',
    yearFiled: '2025',
    tags: ['dubai', 'uae', 'police-clearance', 'employment'],
  },
];

/**
 * Filter cases by category
 * @param {CaseModel[]} cases
 * @param {string} category
 * @returns {CaseModel[]}
 */
export const filterCasesByCategory = (cases, category) => {
  if (!category || category === 'all') return cases;
  return cases.filter(c => c.category === category);
};

/**
 * Filter cases by status
 * @param {CaseModel[]} cases
 * @param {string} status
 * @returns {CaseModel[]}
 */
export const filterCasesByStatus = (cases, status) => {
  if (!status || status === 'all') return cases;
  return cases.filter(c => c.status === status);
};

/**
 * Search cases by text
 * @param {CaseModel[]} cases
 * @param {string} query
 * @returns {CaseModel[]}
 */
export const searchCases = (cases, query) => {
  if (!query) return cases;
  const lowerQuery = query.toLowerCase();
  return cases.filter(c => 
    c.summary.toLowerCase().includes(lowerQuery) ||
    c.outcome.toLowerCase().includes(lowerQuery) ||
    c.tags.some(tag => tag.includes(lowerQuery))
  );
};

export default {
  CaseStatus,
  CaseCategory,
  CaseCategoryLabels,
  SAMPLE_CASES,
  filterCasesByCategory,
  filterCasesByStatus,
  searchCases,
};
